import { Component, createRef, type ErrorInfo, type ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";
import { AlertTriangle } from "lucide-react";
import { Button } from "@/components/Button";
import { captureRouteException } from "@/lib/posthog";

interface ErrorBoundaryProps {
  children: ReactNode;
  /** Optional custom fallback element rendered instead of the default card. */
  fallback?: ReactNode;
}

interface InnerProps extends ErrorBoundaryProps {
  /**
   * Changes to this value clear a tripped boundary (the wrapper passes the
   * current pathname), so navigating away always recovers — never a stuck
   * fallback after the admin leaves the crashed view.
   */
  resetKey: string;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

/**
 * Class error boundary behind {@link ErrorBoundary} (#295, upgraded for #803).
 *
 * Catches render-time exceptions in its subtree and shows a graceful fallback
 * instead of letting a single bad field unmount the whole view (the failure
 * mode behind the company-detail white-screen, #295). Only the error's
 * message is logged — never an arbitrary object dump — to avoid leaking PII
 * from props or component state into logs, and the raw message/stack is never
 * rendered to the user.
 */
class ErrorBoundaryInner extends Component<InnerProps, ErrorBoundaryState> {
  override state: ErrorBoundaryState = { hasError: false };

  private readonly fallbackRef = createRef<HTMLDivElement>();

  /**
   * React lifecycle: switch to the fallback UI when a child throws.
   * @returns The next state flagging the caught error.
   */
  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  /**
   * React lifecycle: log the caught error (message only, no object dump),
   * then move focus to the fallback. Focus is done HERE (not
   * componentDidUpdate) because a crash during the subtree's initial mount
   * commits the fallback in the mount pass — componentDidCatch runs after
   * that commit in both cases, so the ref is always attached.
   * @param error - The thrown error.
   * @param info - React-supplied component stack info (intentionally not logged).
   */
  override componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error(`[admin] Unhandled render error: ${error.message}`);
    // #828: report to PostHog (error + component stack only — see
    // captureRouteException's doc comment for the no-PII contract). No-ops
    // when PostHog isn't configured (e.g. local dev without a key).
    captureRouteException(error, info.componentStack ?? null);
    this.fallbackRef.current?.focus();
  }

  /**
   * React lifecycle: auto-reset a tripped boundary when the route changes.
   * @param prevProps - Previous props (for the resetKey comparison).
   */
  override componentDidUpdate(prevProps: InnerProps): void {
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false });
    }
  }

  /** Reset the boundary so the children get one more chance to render. */
  private readonly handleRetry = (): void => {
    this.setState({ hasError: false });
  };

  /**
   * Render either the fallback (after an error) or the wrapped children.
   * @returns The rendered subtree or fallback.
   */
  override render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback !== undefined) {
        return this.props.fallback;
      }
      return (
        <div className="flex min-h-[50vh] items-center justify-center p-6">
          <div
            ref={this.fallbackRef}
            role="alert"
            tabIndex={-1}
            className="w-full max-w-md rounded-lg border border-border bg-surface p-6 text-center focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
          >
            <AlertTriangle
              className="mx-auto h-5 w-5 text-destructive-700"
              strokeWidth={1.75}
              aria-hidden="true"
            />
            <h2 className="mt-3 text-lg font-semibold text-foreground">
              Couldn&apos;t load this page
            </h2>
            <p className="mt-1 text-sm text-muted-foreground">
              An unexpected error stopped this page from displaying. It has
              been logged for review.
            </p>
            <div className="mt-4 flex items-center justify-center gap-2">
              <Button
                type="button"
                onClick={this.handleRetry}
              >
                Try again
              </Button>
              <Link
                to="/"
                className="inline-flex min-h-11 items-center justify-center rounded-sm bg-neutral-100 px-4 py-2 text-sm font-medium text-neutral-700 hover:bg-neutral-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
              >
                Go home
              </Link>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * Route-level error boundary for the admin app (#295, #803).
 *
 * Mount it around route content: a render crash in the subtree shows a
 * friendly fallback card with "Try again" + "Go home" instead of
 * white-screening, while everything OUTSIDE the boundary stays mounted. The
 * boundary resets itself on route change, so navigating away always recovers.
 *
 * Must be rendered inside a react-router `Router` (it reads the location for
 * the route-change reset).
 *
 * @param props - The component props.
 * @param props.children - The content to guard.
 * @param props.fallback - Optional custom fallback element.
 * @returns The guarded subtree.
 */
export function ErrorBoundary({ children, fallback }: ErrorBoundaryProps): React.ReactElement {
  const location = useLocation();
  return (
    <ErrorBoundaryInner resetKey={location.pathname} fallback={fallback}>
      {children}
    </ErrorBoundaryInner>
  );
}
